(() => {
  const FILL = 0.88;
  const scaleCache = new Map();

  const measureContent = (img) => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0);
    let data;
    try {
      data = ctx.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null;
    }

    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;
    const step = Math.max(1, Math.floor(Math.min(width, height) / 160));

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const i = (y * width + x) * 4;
        const alpha = data[i + 3];
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];
        if (alpha < 20 || (red > 245 && green > 245 && blue > 245)) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (maxX < minX || maxY < minY) return null;

    return {
      width,
      height,
      contentWidth: maxX - minX + 1,
      contentHeight: maxY - minY + 1,
    };
  };

  const scaleForImage = (img) => {
    const key = img.currentSrc || img.src;
    if (scaleCache.has(key)) return scaleCache.get(key);

    const frame = img.closest('.bayaan-designers__logo-frame');
    if (!frame) {
      scaleCache.set(key, 1);
      return 1;
    }

    const bounds = measureContent(img);
    if (!bounds) {
      scaleCache.set(key, 1);
      return 1;
    }

    const frameW = frame.clientWidth || 1;
    const frameH = frame.clientHeight || 1;
    const contain = Math.min(frameW / bounds.width, frameH / bounds.height);
    const shownW = bounds.contentWidth * contain;
    const shownH = bounds.contentHeight * contain;
    if (!shownW || !shownH) {
      scaleCache.set(key, 1);
      return 1;
    }

    const boost = Math.min((frameW * FILL) / shownW, (frameH * FILL) / shownH);
    const scale = Math.min(Math.max(boost, 1), 2.8);
    scaleCache.set(key, scale);
    return scale;
  };

  const applyScale = (img, scale) => {
    img.style.transform = scale === 1 ? '' : `scale(${scale})`;
    img.classList.add('is-ready');
  };

  const revealLogo = (img, root) => {
    const key = img.currentSrc || img.src;
    const scale = scaleForImage(img);
    applyScale(img, scale);

    root.querySelectorAll('.bayaan-designers__logo').forEach((other) => {
      if (other === img || other.classList.contains('is-ready')) return;
      const otherKey = other.currentSrc || other.src;
      if (otherKey === key) applyScale(other, scale);
    });
  };

  const normalizeLogos = (root) => {
    root.querySelectorAll('.bayaan-designers__logo').forEach((img) => {
      if (img.classList.contains('is-ready')) return;

      const show = () => revealLogo(img, root);
      if (img.complete && img.naturalWidth) {
        show();
      } else {
        img.addEventListener('load', show, { once: true });
        img.addEventListener('error', () => img.classList.add('is-ready'), { once: true });
      }
    });
  };

  const setupMarquee = (root) => {
    const track = root.querySelector('.bayaan-designers__track');
    const firstList = track?.querySelector('.bayaan-designers__list');
    if (!track || !firstList) return;

    while (track.children.length > 1) {
      track.removeChild(track.lastElementChild);
    }

    let tries = 0;
    const ensureCopies = () => {
      const shift = firstList.offsetWidth;
      if (!shift && tries < 20) {
        tries += 1;
        requestAnimationFrame(ensureCopies);
        return;
      }
      if (!shift) return;

      while (track.children.length < 2 || track.scrollWidth < shift * 2) {
        const clone = firstList.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('.bayaan-designers__logo').forEach((img) => {
          img.classList.remove('is-ready');
          img.style.transform = '';
        });
        track.appendChild(clone);
      }

      track.style.setProperty('--bayaan-marquee-shift', `${firstList.offsetWidth}px`);
      normalizeLogos(root);
    };

    ensureCopies();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        track.style.setProperty('--bayaan-marquee-shift', `${firstList.offsetWidth}px`);
      });
      observer.observe(firstList);
      root._bayaanMarqueeObserver = observer;
    }
  };

  const setupToggle = (root) => {
    const toggle = root.querySelector('[data-bayaan-designers-toggle]');
    const panel = root.querySelector('[data-bayaan-designers-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (open) {
        panel.setAttribute('hidden', '');
        panel.classList.remove('is-open');
      } else {
        panel.removeAttribute('hidden');
        panel.classList.add('is-open');
      }
    });
  };

  const init = (root) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';
    setupToggle(root);
    setupMarquee(root);
  };

  const initAll = () => {
    document.querySelectorAll('[data-bayaan-designers]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root =
      event.target.querySelector?.('[data-bayaan-designers]') ||
      event.target.closest?.('[data-bayaan-designers]');
    if (!root) return;
    if (root._bayaanMarqueeObserver) {
      root._bayaanMarqueeObserver.disconnect();
      delete root._bayaanMarqueeObserver;
    }
    scaleCache.clear();
    delete root.dataset.ready;
    init(root);
  });
})();
